import { useEffect, useState, useMemo } from "react";
import { Col, Container as Grid, Row, Card, Modal, Form, Table } from "react-bootstrap";
import { Button } from "components/core/basic/Button";
import { useDates } from "dates/DatesContext";
import Breadcrumb from "components/core/blocks/Breadcrumb";
import { DatesProvider } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import moment from "moment";
import { formatCurrency, formatPercent } from "utils/formatNumber";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import Loading from "components/core/blocks/Loading";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import Accordion from "components/core/basic/Accordion";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/treemap";
import { trellisPalette } from "components/custom/analytics/palettes";
import { HELP_TEXT } from "./constants";
import ToolTips from "utils/toolTips";
import ImportButton from "components/custom/import/ImportButton";
import useQueryParams from "hooks/useQueryParams";

interface CurrencyFormatProps {
  value: number | null;
}

interface ButtonBucket {
  label: string;
  value: string;
  counter?: number;
}

interface LineItem {
  label: string;
  amount: number;
  is_tax: boolean;
}

interface LineItems {
  ad_spend: LineItem[];
  misc: LineItem[];
  orders: LineItem[];
  promo: LineItem[];
  refunds: LineItem[];
  warehouse: LineItem[];
  other: LineItem[];
  total: number;
}

interface LineItemsDict {
  [key: string]: LineItems;
}

// Define Type for data
interface Data {
  line_items: LineItemsDict;
  no_landed_cost_count: number;
  report_end_date: string;
}

interface TreeMapValues {
  advertising: number;
  landed_cost: number;
  tax: number;
  fba: number;
  referral: number;
  shipping: number;
  warehouse: number;
  promo: number;
  other: number;
}

const Dashboard = () => {
  const lastDayThisMonth = moment().endOf("month");
  const firstDayThreeMonthsAgo = moment().subtract(3, "months").startOf("month");

  const [asinQuery] = useQueryParams("asin", "");
  const [productId] = useQueryParams("product_id", "");

  const identifier = asinQuery || productId;

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [showFiguresModal, setShowFiguresModal] = useState<boolean>(false);
  const [asinText, setAsinText] = useState<string>(identifier || "");
  const [asinFilter, setASINFilter] = useState<string | null>(identifier || null);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<number>(0);
  const { group, start, end, preStart, preEnd } = useDates();

  const getDashboardTo = () => {
    const url = `/user/pl/report`;

    return url;
  };

  const currencyFormat = ({ value }: CurrencyFormatProps): string => {
    if (value === null) {
      return "-";
    }

    return formatCurrency(value, marketPlace.marketPlace, true, false, false);
  };

  const breadCrumbItems = [
    {
      name: "Profit and Expenses Report",
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: "Dashboards",
    },
  ];

  const filters = {
    market_place: marketPlace.marketPlace,
    channel,
    date_group: group,
    start: firstDayThreeMonthsAgo.format("YYYY-MM-DD"),
    end: lastDayThisMonth.format("YYYY-MM-DD"),
    empty: false,
    group_by: "org_id",
    order_association: asinFilter !== null,
    identifier: asinFilter,
  };

  const { data, isLoading, isFetching } = useFetch<Data>(
    ["profit_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, refresh],
    `/merchandise/settlement_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: true,
    },
  );

  const buttunBuckets: ButtonBucket[] = useMemo(() => {
    if (!data || Object.keys(data["line_items"]).length === 0) {
      return [];
    }

    const monthYearKeys = Object.keys(data["line_items"]);

    const buttunBuckets: ButtonBucket[] = [];

    monthYearKeys.forEach((key) => {
      // convert key of type MM-YYYY to MMMM YYYY
      const dt = moment(key, "MM-YYYY");
      const monthYear = dt.format("MMM YYYY");

      buttunBuckets.push({
        label: monthYear,
        value: key,
        counter: undefined,
      });
    });

    return buttunBuckets;
  }, [data]);

  useEffect(() => {
    if (data && Object.keys(data["line_items"]).length > 0) {
      const keys = Object.keys(data["line_items"]);
      const lastKey = keys[keys.length - 1];
      if (lastKey === undefined) {
        return;
      }
      setSelectedMonth(lastKey);
    }
  }, [data]);

  const { barChartOptions, treeMapOptions } = useMemo(() => {
    const getBarValues = (data: LineItems): { sales: number; advertising: number; costOfSelling: number } => {
      let sales: number = 0;
      let advertising: number = 0;
      let costOfSelling: number = 0;

      const orders: LineItem[] = data.orders;
      orders.forEach((item) => {
        if (item.label === "Orders") {
          sales += item.amount;
        }
      });
      data.refunds.forEach((item) => {
        if (item.label === "Orders") {
          sales += item.amount;
        }
      });
      data.ad_spend.forEach((item) => {
        if (item.label !== "Sub Total") {
          advertising += item.amount;
        }
      });

      const keys = Object.keys(data) as (keyof LineItems)[];
      keys.forEach((key) => {
        if (key === "ad_spend" || key === "total") {
          return;
        }
        data[key].forEach((item) => {
          if (item.label === "Sub Total" || item.label === "Orders" || item.label.includes("Tax")) {
            return;
          }
          costOfSelling += item.amount;
        });
      });

      return {
        sales,
        advertising,
        costOfSelling,
      };
    };

    const getTreeMapValues = (data: LineItems): TreeMapValues => {
      const keys = Object.keys(data) as (keyof LineItems)[];

      const treeMapValues: TreeMapValues = {
        advertising: 0,
        landed_cost: 0,
        tax: 0,
        fba: 0,
        referral: 0,
        shipping: 0,
        warehouse: 0,
        promo: 0,
        other: 0,
      };

      keys.forEach((key) => {
        if (key === "total") {
          return;
        }
        for (let i = 0; i < data[key].length; i++) {
          const item: LineItem | undefined = data[key][i];
          if (item === undefined) {
            continue;
          }
          if (item.label === "Sub Total" || item.label === "Orders") {
            continue;
          }
          const label = item.label;

          if (key === "ad_spend") {
            treeMapValues.advertising += -item.amount;
          } else if (label.includes("Tax")) {
            treeMapValues["tax"] += -item.amount;
          } else if (label.toLowerCase().includes("fba")) {
            treeMapValues["fba"] += -item.amount;
          } else if (label.toLowerCase().includes("referral") || label.toLowerCase().includes("commission")) {
            treeMapValues["referral"] += -item.amount;
          } else if (label.toLowerCase().includes("shipping")) {
            treeMapValues["shipping"] += -item.amount;
          } else if (label.toLowerCase().includes("landed cost") || label.toLowerCase().includes("additional cost")) {
            treeMapValues["landed_cost"] += -item.amount;
          } else if (key === "warehouse" || label.toLowerCase().includes("restocking")) {
            treeMapValues["warehouse"] += -item.amount;
          } else if (
            key === "promo" ||
            label.toLowerCase().includes("promo") ||
            label.toLowerCase().includes("coupon")
          ) {
            treeMapValues["promo"] += -item.amount;
          } else {
            treeMapValues["other"] += -item.amount;
          }
        }
      });

      return treeMapValues;
    };

    if (!data || Object.keys(data["line_items"]).length === 0 || selectedMonth === null) {
      return {
        barChartOptions: null,
        treeMapOptions: null,
      };
    }

    const monthYears = Object.keys(data["line_items"]);

    const sales = [];
    const advertising = [];
    const costOfSelling = [];
    const profit = [];
    const treeMap: { [key: string]: TreeMapValues } = {};

    for (let i = 0; i < monthYears.length; i++) {
      const my: string | undefined = monthYears[i];
      if (my === undefined) {
        continue;
      }
      const monthData: LineItems | undefined = data.line_items[my];
      if (monthData === undefined) {
        continue;
      }
      const myRes = getBarValues(monthData);
      const tmRes = getTreeMapValues(monthData);
      treeMap[my] = tmRes;
      sales.push(myRes.sales);
      advertising.push(-myRes.advertising);
      costOfSelling.push(-myRes.costOfSelling);
      profit.push(myRes.sales + myRes.costOfSelling + myRes.advertising);
    }

    const barChartOptions = {
      chart: {
        type: "column",
        style: {
          "font-family": "Poppins",
          "font-weight": 500,
          "font-size": "15px",
        },
      },
      title: {
        text: null,
      },
      subtitle: null,
      xAxis: {
        categories: monthYears.map((my) => moment(my, "MM-YYYY").format("MMM YYYY")),
        crosshair: true,
        accessibility: {
          description: "Months",
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "($)",
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
        },
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point): string {
          return `${
            this.series.name
          }: <b>${this.y !== undefined ? formatCurrency(this.y, marketPlace.marketPlace, true, false, false) : "-"}</b><br/>`;
        },
      },
      series: [
        {
          name: "Sales",
          data: sales,
          color: trellisPalette[1],
        },
        {
          name: "Cost of Selling",
          data: costOfSelling,
          color: trellisPalette[3],
        },
        {
          name: "Advertising",
          data: advertising,
          color: trellisPalette[2],
        },
        {
          name: "Profit",
          data: profit,
          color: trellisPalette[12],
        },
      ],
    };

    const treeMapData = treeMap[selectedMonth];
    const treeMapOptions = {
      // tooltip: {
      //   pointFormatter: function () {
      //     return `<span style="color:${this.color}">\u25CF</span> ${
      //       this.series.name
      //     }: <b>${formatCurrency(this.y, marketPlace.marketPlace, true, false, false)}</b><br/>`;
      //   },
      // },
      chart: {
        type: "treemap",
        style: {
          "font-family": "Poppins",
          "font-weight": 500,
          "font-size": "15px",
        },
      },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          data: [
            {
              name: "Advertising",
              value: treeMapData ? treeMapData.advertising : 0,
              colorValue: 1,
              color: trellisPalette[2],
            },
            {
              name: "Landed Cost",
              value: treeMapData ? treeMapData.landed_cost : 0,
              colorValue: 1,
              color: trellisPalette[3],
            },
            {
              name: "Tax",
              value: treeMapData ? treeMapData.tax : 0,
              colorValue: 1,
              color: trellisPalette[4],
            },
            {
              name: "FBA",
              value: treeMapData ? treeMapData.fba : 0,
              colorValue: 1,
              color: trellisPalette[5],
            },
            {
              name: "Referral",
              value: treeMapData ? treeMapData.referral : 0,
              colorValue: 1,
              color: trellisPalette[6],
            },
            {
              name: "Shipping",
              value: treeMapData ? treeMapData.shipping : 0,
              colorValue: 1,
              color: trellisPalette[7],
            },
            {
              name: "Warehouse",
              value: treeMapData ? treeMapData.warehouse : 0,
              colorValue: 1,
              color: trellisPalette[8],
            },
            {
              name: "Promotion",
              value: treeMapData ? treeMapData.promo : 0,
              colorValue: 1,
              color: trellisPalette[9],
            },
            {
              name: "Other",
              value: treeMapData ? treeMapData.other : 0,
              colorValue: 1,
              color: trellisPalette[10],
            },
          ],
        },
      ],
      title: {
        text: null,
      },
    };

    return { barChartOptions, treeMapOptions };
  }, [data, marketPlace, selectedMonth]);

  const TransactionTable = ({ transactions, title }: { transactions: LineItem[]; title: string }) => {
    return (
      <Table striped bordered hover size="sm" className="mb-3">
        <thead>
          <tr>
            <th className="text-start">{title}</th>
            <th className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((item, index) => {
            if (item.is_tax) {
              return null;
            }
            return (
              <tr
                key={index}
                style={{ borderTop: item.label === "Sub Total" ? "3px solid #555" : "1px solid #dee2e6" }}
              >
                <td>
                  {item.label}
                  <ToolTips id={item.label} toolTip={HELP_TEXT[item.label as keyof typeof HELP_TEXT]} />
                </td>
                <td className="text-end">{currencyFormat({ value: item.amount })}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const Margin = ({ total, lineItems }: { total: number; lineItems: LineItems }) => {
    if (total === 0) {
      return "0.00%";
    }

    let orderPrincipal = 0;
    for (let i = 0; i < lineItems["orders"].length; i++) {
      const li = lineItems["orders"][i];
      if (li?.label === "Orders") {
        orderPrincipal += li.amount;
        break;
      }
    }
    for (let i = 0; i < lineItems["refunds"].length; i++) {
      const li = lineItems["refunds"][i];
      if (li?.label === "Orders") {
        orderPrincipal += li.amount;
        break;
      }
    }
    return `${formatPercent(total / orderPrincipal, 2, false)}`;
  };

  if (isLoading || isFetching) {
    return <Loading fullPage={false} />;
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Breadcrumb items={breadCrumbItems} />
      {data && data.no_landed_cost_count > 0 && (
        <Row style={{ width: "calc(100vw - 95px)" }}>
          <Col className="p-0 m-0" xs={12}>
            <div style={{ backgroundColor: trellisPalette[3], color: "white", padding: "1rem" }} className="fs-4 px-4">
              <Row>
                <Col className="pt-2" xs={10}>
                  There are <strong>{data.no_landed_cost_count}</strong> or more productst that do not have landed cost
                  data. To imporve the accuracy of your profit and expenses report, please upload your landed cost data.
                </Col>
                <Col className="text-end" xs={2}>
                  <ImportButton
                    defaultType={`pricing_bulk_upload`}
                    importModalOpen={importModalOpen}
                    setImportModalOpen={setImportModalOpen}
                    title={"Upload Pricing Data"}
                    style={{
                      border: 0,
                      backgroundColor: trellisPalette[2],
                      color: "white",
                    }}
                    noMargin
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}
      <div className="mb-4"> </div>
      {selectedMonth && data?.report_end_date && (
        <Row>
          <Col className="px-5" xs={12}>
            <Card>
              <Card.Body style={{ padding: "2rem" }}>
                <Row>
                  <Col xs={6}>
                    <ButtonGroup
                      buttons={buttunBuckets}
                      value={selectedMonth}
                      onSelect={(values) => {
                        setSelectedMonth(values);
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Row>
                      <Col className="pb-3" xs={6}></Col>
                      <Col className="text-end pb-3" xs={6}>
                        <Row>
                          <Col xs={9}>
                            <Form.Control
                              type="text"
                              id="asin"
                              aria-describedby="passwordHelpBlock"
                              placeholder="Enter ASIN or Parent ASIN"
                              defaultValue={asinText}
                              onChange={(e) => {
                                setAsinText(e.target.value);
                              }}
                            />
                          </Col>
                          <Col xs={3}>
                            <Button
                              onPress={() => {
                                setASINFilter(asinText);
                                setRefresh((prev) => prev + 1);
                              }}
                            >
                              {asinFilter && !asinText ? `Resest` : `Filter`}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      <Col className="pt-2 ps-4 text-end" xs={12}></Col>
                    </Row>
                  </Col>
                  <Col xs={6}>
                    <div
                      className="fs-4 fw-bold"
                      style={{
                        marginTop: "0rem",
                        marginBottom: "0.2rem",
                        textTransform: "uppercase",
                        color: trellisPalette[4],
                      }}
                    >
                      Profit and Expenses
                    </div>
                    <small>
                      {firstDayThreeMonthsAgo.format("MMMM YYYY")} - {lastDayThisMonth.format("MMMM YYYY")}
                    </small>
                    <div className="mt-3 pe-3">
                      {barChartOptions && <HighchartsReact highcharts={Highcharts} options={barChartOptions} />}
                    </div>

                    <div
                      className="fs-4 fw-bold"
                      style={{
                        marginTop: "0rem",
                        marginBottom: "0.2rem",
                        textTransform: "uppercase",
                        color: trellisPalette[4],
                      }}
                    >
                      Expense Breakdown
                    </div>
                    <small>{moment(selectedMonth, "MM-YYYY").format("MMMM YYYY")}</small>
                    <div className="mt-3 pe-3">
                      {treeMapOptions && <HighchartsReact highcharts={Highcharts} options={treeMapOptions} />}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <Row>
                      <Col xs={6}>
                        <div
                          className="fs-4 fw-bold text-end"
                          style={{
                            marginTop: "0rem",
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                            color: trellisPalette[4],
                          }}
                        >
                          &nbsp;
                        </div>
                        {data.report_end_date && (
                          <div className="fs-5 fw-normal text-start">
                            Latest Settlement Report: {moment(data["report_end_date"]).format("MMMM DD YYYY")}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <div
                          className="fs-4 fw-bold text-end"
                          style={{
                            marginTop: "0rem",
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                            color: trellisPalette[4],
                          }}
                        >
                          <span>
                            Profit:{" "}
                            {selectedMonth &&
                              data.line_items[selectedMonth] &&
                              currencyFormat({ value: data.line_items[selectedMonth].total })}
                          </span>
                          <span className="mx-2">-</span>
                          <span>
                            {data.line_items[selectedMonth] && (
                              <Margin
                                total={data.line_items[selectedMonth]["total"]}
                                lineItems={data.line_items[selectedMonth]}
                              />
                            )}
                          </span>
                        </div>

                        <div className="fs-5 fw-normal text-end">
                          <span
                            style={{ color: trellisPalette[0], cursor: "pointer" }}
                            onClick={() => setShowFiguresModal(true)}
                          >
                            How are these figures calculated?
                          </span>
                        </div>
                      </Col>
                    </Row>

                    {data.line_items[selectedMonth] && (
                      <>
                        <Accordion title="Orders" initialExpanded marginTop={`0.5rem`}>
                          <TransactionTable transactions={data.line_items[selectedMonth].orders} title="Line Item" />
                        </Accordion>
                        <Accordion title="Refunds">
                          <TransactionTable transactions={data.line_items[selectedMonth].refunds} title="Line Item" />
                        </Accordion>
                        <Accordion title="Advertisement Spend">
                          <TransactionTable transactions={data.line_items[selectedMonth]["ad_spend"]} title="Ad Type" />
                        </Accordion>
                        {!asinFilter && (
                          <Accordion title="Warehouse">
                            <TransactionTable
                              transactions={data.line_items[selectedMonth].warehouse}
                              title="Line Item"
                            />
                          </Accordion>
                        )}
                        {!asinFilter && (
                          <Accordion title="Promo">
                            <TransactionTable transactions={data.line_items[selectedMonth].promo} title="Line Item" />
                          </Accordion>
                        )}

                        <Accordion title="Miscellaneous">
                          <TransactionTable transactions={data.line_items[selectedMonth].other} title="Line Item" />
                        </Accordion>
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {selectedMonth === null && (
        <Row>
          <Col className="px-5 py-6 text-center" xs={12}>
            {asinFilter && (
              <Row>
                <Col xs={12}>
                  <span className="fs-3">The ASIN or Parent ASIN you have chosen was not found.</span>
                </Col>
                <Col className="pt-3" xs={12}>
                  <span
                    className="fs-3"
                    style={{ cursor: "pointer", color: trellisPalette[0] }}
                    onClick={() => {
                      setASINFilter(null);
                      setAsinText("");
                      setRefresh((prev) => prev + 1);
                    }}
                  >
                    Reset Filter
                  </span>
                </Col>
              </Row>
            )}
            {asinFilter === null && (
              <span className="fs-3">The Profit and Expenses Reports have not yet been generated.</span>
            )}
          </Col>
        </Row>
      )}
      <Modal show={showFiguresModal} onHide={() => setShowFiguresModal(false)} size={"lg"}>
        <Modal.Header closeButton>
          <span className="fs-2">How are these figures calculated?</span>
        </Modal.Header>
        <Modal.Body>
          <div className="p-4">
            <p>
              These figures come from Amazon’s bi-weekly Settlement Reports. We groupt the different line items from the
              settlment report and display them in categories.
            </p>
            <ul className="fs-4">
              <li>
                <strong>Order-Linked Items</strong>: Orders, Refunds, and Advertising spend are tied directly to orders
              </li>
              <li>
                <strong>General Fees</strong>: Warehouse Fees, Promo, and Miscellaneous fees are only visible in monthly
                reports as they aren’t order-specific.
              </li>
            </ul>
            <p>For greater accuracy, we recommend uploading your landed costs.</p>
          </div>
        </Modal.Body>
      </Modal>
    </Grid>
  );
};

const PLDashboard = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default PLDashboard;
